import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, TextField, Typography,
} from '@mui/material';

import AppDialog from '../../../shared/components/AppDialog';
import { useCreateOrgCreationRequest, useMyOrgCreationRequests } from '../hooks';
import type { OrgCreationRequestParams } from '../hooks';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmitted: (message: string) => void;
}

const BLANK: OrgCreationRequestParams = {
  name: '',
  slug: '',
  billing_email: '',
  headquarters_address: '',
  headquarters_city: '',
  headquarters_state: '',
  headquarters_zip: '',
  headquarters_country: 'US',
  headquarters_phone: '',
  reason: '',
};

export default function RequestNewOrgDialog({ open, onClose, onSubmitted }: Props) {
  const [form, setForm] = useState<OrgCreationRequestParams>(BLANK);
  const create = useCreateOrgCreationRequest();
  const { data: mineData } = useMyOrgCreationRequests(open);

  useEffect(() => {
    if (!open) {
      setForm(BLANK);
      create.reset();
    }
  }, [open, create]);

  const setField = <K extends keyof OrgCreationRequestParams>(key: K, value: OrgCreationRequestParams[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const pendingExisting = useMemo(
    () => mineData?.result.find((r) => r.status === 'pending'),
    [mineData],
  );

  const handleSubmit = () => {
    const name = form.name.trim();
    if (!name) return;
    const trim = (s?: string) => {
      const v = s?.trim();
      return v ? v : undefined;
    };
    const payload: OrgCreationRequestParams = {
      name,
      slug: trim(form.slug),
      billing_email: trim(form.billing_email),
      headquarters_address: trim(form.headquarters_address),
      headquarters_city: trim(form.headquarters_city),
      headquarters_state: trim(form.headquarters_state),
      headquarters_zip: trim(form.headquarters_zip),
      headquarters_country: trim(form.headquarters_country),
      headquarters_phone: trim(form.headquarters_phone),
      reason: trim(form.reason),
    };
    create.mutate(payload, {
      onSuccess: () => {
        onSubmitted(`Request submitted for "${name}". A system admin will review it.`);
        onClose();
      },
    });
  };

  const error = create.error instanceof Error ? create.error.message : null;

  return (
    <AppDialog
      open={open}
      onClose={() => { if (!create.isPending) onClose(); }}
      title="Request a new organization"
      maxWidth="sm"
      actions={
        <>
          <Button onClick={onClose} disabled={create.isPending}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!form.name.trim() || create.isPending || !!pendingExisting}
          >
            {create.isPending ? 'Submitting…' : 'Submit request'}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'grid', gap: 2 }}>
        {pendingExisting && (
          <Alert severity="info">
            You already have a pending request for <strong>{pendingExisting.name}</strong>. A system admin will review it before you can submit another.
          </Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}

        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          We'll keep the request paused until a system admin approves it. On approval, the
          organization is created and you become its owner — you can edit billing and HQ
          details in org settings afterwards.
        </Typography>

        <TextField
          label="Organization name"
          value={form.name}
          onChange={(e) => setField('name', e.target.value)}
          fullWidth required autoFocus
        />
        <TextField
          label="Slug (optional)"
          value={form.slug ?? ''}
          onChange={(e) => setField('slug', e.target.value)}
          helperText="Lowercase URL handle. Auto-generated from the name if left blank."
          fullWidth
        />
        <TextField
          label="Billing email (optional)"
          type="email"
          value={form.billing_email ?? ''}
          onChange={(e) => setField('billing_email', e.target.value)}
          helperText="Defaults to your own email on approval."
          fullWidth
        />

        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mt: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Headquarters (optional)
        </Typography>
        <TextField
          label="Street address"
          value={form.headquarters_address ?? ''}
          onChange={(e) => setField('headquarters_address', e.target.value)}
          fullWidth
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr' }, gap: 1.5 }}>
          <TextField
            label="City"
            value={form.headquarters_city ?? ''}
            onChange={(e) => setField('headquarters_city', e.target.value)}
            fullWidth
          />
          <TextField
            label="State"
            value={form.headquarters_state ?? ''}
            onChange={(e) => setField('headquarters_state', e.target.value)}
            fullWidth
          />
          <TextField
            label="ZIP"
            value={form.headquarters_zip ?? ''}
            onChange={(e) => setField('headquarters_zip', e.target.value)}
            fullWidth
          />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr' }, gap: 1.5 }}>
          <TextField
            label="Country"
            value={form.headquarters_country ?? 'US'}
            onChange={(e) => setField('headquarters_country', e.target.value)}
            fullWidth
          />
          <TextField
            label="Phone"
            value={form.headquarters_phone ?? ''}
            onChange={(e) => setField('headquarters_phone', e.target.value)}
            fullWidth
          />
        </Box>

        <TextField
          label="Reason for the request"
          multiline minRows={3}
          value={form.reason ?? ''}
          onChange={(e) => setField('reason', e.target.value)}
          helperText="Shown to the system admin reviewing this request."
          fullWidth
        />
      </Box>
    </AppDialog>
  );
}
